import { useCallback, useEffect, useState } from "react";
import { fetchWallets, fetchWalletDetail, fetchTransferEstimate, createTransferDraft, submitTransferDraft } from "@/lib/api";
import type { TransferWalletOption, TransferAssetOption } from "@/types";

interface TransferDataResult {
  sourceWallets: TransferWalletOption[];
  destWallets: TransferWalletOption[];
  assets: TransferAssetOption[];
  estimate: { networkFee: string; providerFee: string; totalFee: string } | null;
  estimateLoading: boolean;
  walletsLoading: boolean;
  assetsLoading: boolean;
  loadAssetsForWallet: (walletId: string) => void;
  loadEstimate: (params: { sourceWalletId: string; destinationAddress: string; asset: string; amount: string }) => void;
  submitTransfer: (draft: {
    sourceWalletId: string; destinationAddress: string;
    asset: string; amount: string; network: string;
  }) => Promise<{ ok: boolean; transferId?: string; error?: string }>;
}

function mapWalletToOption(w: Record<string, unknown>): TransferWalletOption {
  const id = String(w.walletId ?? w.id ?? "");
  const alias = String(w.accountAlias ?? w.name ?? id);
  const chain = String(w.chain ?? "—");
  const network = String(w.network ?? "");
  const profile = String(w.walletProfileId ?? "");
  const meta = [chain, network, profile].filter(Boolean).join(" · ");
  return { id, name: alias || id, meta };
}

export function useTransferData(): TransferDataResult {
  const [sourceWallets, setSourceWallets] = useState<TransferWalletOption[]>([]);
  const [destWallets, setDestWallets] = useState<TransferWalletOption[]>([]);
  const [assets, setAssets] = useState<TransferAssetOption[]>([]);
  const [estimate, setEstimate] = useState<TransferDataResult["estimate"]>(null);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setWalletsLoading(true);
      const result = await fetchWallets();
      if (cancelled) return;
      const mapped = result.wallets.map(mapWalletToOption);
      setSourceWallets(mapped);
      setDestWallets(mapped);
      setWalletsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const loadAssetsForWallet = useCallback((walletId: string) => {
    let cancelled = false;
    (async () => {
      setAssetsLoading(true);
      setAssets([]);
      const result = await fetchWalletDetail(walletId);
      if (cancelled) return;
      if (result.wallet) {
        const raw = result.wallet as Record<string, unknown>;
        const balances = Array.isArray(raw.balances) ? raw.balances as Record<string, unknown>[] : [];
        const wallet = (raw.wallet && typeof raw.wallet === "object" && !Array.isArray(raw.wallet))
          ? raw.wallet as Record<string, unknown>
          : raw;
        const chain = String(wallet.chain ?? "—");
        const network = String(wallet.network ?? "—");
        const profile = String(wallet.walletProfileId ?? "—");
        setAssets(balances.map(b => ({
          symbol: String(b.symbol ?? b.asset ?? ""),
          name: String(b.name ?? b.symbol ?? b.asset ?? ""),
          balance: String(b.amount ?? b.balance ?? "0"),
          network: `${chain} ${network}`,
          route: profile,
        })));
      }
      setAssetsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const loadEstimate = useCallback((params: {
    sourceWalletId: string; destinationAddress: string; asset: string; amount: string;
  }) => {
    let cancelled = false;
    (async () => {
      setEstimateLoading(true);
      setEstimate(null);
      const result = await fetchTransferEstimate(params);
      if (cancelled) return;
      if (result.estimate) {
        const e = result.estimate as Record<string, unknown>;
        const fee = e.estimatedFee as Record<string, unknown> | undefined;
        const breakdown = fee?.breakdown as Record<string, unknown> | undefined;
        setEstimate({
          networkFee: String(breakdown?.networkFee ?? "—"),
          providerFee: String(breakdown?.platformFee ?? "0"),
          totalFee: `${String(fee?.amount ?? "—")} ${String(fee?.asset ?? "")}`,
        });
      }
      setEstimateLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const submitTransfer = useCallback(async (draft: {
    sourceWalletId: string; destinationAddress: string;
    asset: string; amount: string; network: string;
  }): Promise<{ ok: boolean; transferId?: string; error?: string }> => {
    const createResult = await createTransferDraft(draft);
    if (!createResult.ok) return { ok: false, error: createResult.error };
    const draftId = createResult.transferId!;
    const submitResult = await submitTransferDraft(draftId);
    if (!submitResult.ok) return { ok: false, transferId: draftId, error: submitResult.error };
    return { ok: true, transferId: draftId };
  }, []);

  return {
    sourceWallets, destWallets, assets, estimate, estimateLoading,
    walletsLoading, assetsLoading, loadAssetsForWallet, loadEstimate,
    submitTransfer,
  };
}
